/**
 * Реестр ключей телеметрии карты.
 *
 * Пакет телеметрии сам добавляет префикс `browser.microservice.map-search-frontend`,
 * поэтому здесь ключи начинаются сразу с домена.
 *
 * Разрезы дописываются не ко всем ключам одинаково, см. createMapTelemetry:
 * большинство ключей получает `<deviceType>`, а вспомогательные таймеры для разбора
 * не получают разрезов вовсе.
 *
 * Причина в цене: один таймер в Graphite это 19 whisper-файлов примерно по 1,27 МБ,
 * они выделяются целиком при создании и живут пять лет. Сэмплирование тут не помогает —
 * оно снижает трафик, но не количество заведённых файлов, поэтому новый разрез
 * стоит добавлять только если по нему действительно будут смотреть.
 *
 * Регион, зум и маршрут в ключи не добавляем: первые два по решению Q13, маршрут — потому что
 * /map-novostroyki/ открывают в тысячу раз реже основной карты, и разрез дал бы только
 * пустые серии.
 */
export const TELEMETRY_KEYS = {
  /**
   * SLI 1: сквозное время от начала навигации до отрисованных пинов.
   * Считается одной метрикой, а не суммой слагаемых — перцентили не складываются.
   */
  mapLoad: 'map.load.duration',
  /** Слагаемое для разбора: сколько от начала навигации заняло всё до готовности карты */
  mapInit: 'map.init.duration',
  /**
   * SLI 2: от окончания взаимодействия с картой до отрисованных обновлённых пинов.
   * К ключу дописывается триггер взаимодействия.
   *
   * Сюда попадают и взаимодействия, которым догрузка не понадобилась: всё видимое уже было
   * загружено, и пины корректны почти сразу. Без них в выборке остались бы одни промахи мимо
   * загруженных тайлов, и рост перцентиля нельзя было бы отличить от роста доли таких промахов.
   */
  pinsUpdate: 'map.pins-update.duration',
  /**
   * Сколько из времени SLI 2 пришлось на наш собственный троттлинг до старта запроса.
   * Решение Q3: троттлинг входит в бюджет, но должен быть виден отдельно.
   *
   * Отправляется не при каждом обновлении пинов: троттлинг boundschange срабатывает по переднему
   * фронту, то есть прямо во время перетаскивания, и тогда запрос уходит раньше, чем
   * взаимодействие закончилось, — задержки просто нет. Считать такие случаи нулями нельзя:
   * они завалили бы перцентиль вниз и спрятали настоящие задержки.
   */
  pinsUpdateDelay: 'map.pins-update.delay',
  /**
   * SLI 6: сколько заняла отрисовка одного тайла с пинами.
   * К ключу дописывается бакет по количеству пинов — по образцу android PinBatchLoadDuration,
   * который тоже разделён по количеству. Поштучного замера на пин в canvas сделать нельзя.
   */
  pinsTileRender: 'pins.tile-render.duration',
  /** SLI 7: пин не удалось отрисовать */
  pinsRenderError: 'pins.render.error',
  /** SLI 9: сколько пинов пришло от бэкенда */
  pinsDelivered: 'pins.delivered',
  /** SLI 9: сколько из них дошло до стора пригодными к отрисовке */
  pinsPrepared: 'pins.prepared',
  /** По клику на пин начали открывать выдачу. Показалась ли она — зона ответственности выдачи */
  pinResultsAttempt: 'pin.results.attempt',
  /** Мобильный веб: браузер заблокировал вкладку с выдачей */
  pinResultsPopupBlocked: 'pin.results.popup-blocked',
  /** SLI 8: пин пришёл без данных, без которых его нельзя отрисовать */
  pinsDataInvalid: 'pins.data-invalid',
  /** Слагаемое для разбора: сколько отвечала ручка кластеров */
  clustersRequest: 'clusters.request.duration',
  /** Кластеры доехали до пользователя */
  clustersSucceed: 'clusters.success',
  /** Агрегированный отказ, по нему считается SLO */
  clustersFail: 'clusters.fail',
  /** Сегментация отказа по причине, для скорости разбора */
  clustersFailReason: 'clusters.fail-reason',
  /** Запрос отменён пользователем, в SLI не попадает */
  clustersCancelled: 'clusters.cancelled',
  /** Повторная попытка запроса, ранний сигнал деградации бэкенда */
  clustersRetry: 'clusters.retry',
} as const;

/**
 * Причины отказа при загрузке кластеров.
 *
 * Отмена запроса причиной не является: пользователь просто продолжил двигать карту,
 * такие события считаются отдельным счётчиком и в success/fail не попадают.
 */
export enum EClustersFailReason {
  /** Не дождались ответа */
  Timeout = 'timeout',
  /** Ответ пришёл, но распарсить его не удалось */
  BadPayload = 'badPayload',
  /** Бэкенд явно ответил, что запрос невалиден — например, новый фильтр разошёлся с контрактом */
  BadRequest = 'badRequest',
  /** Прочие 4xx */
  Http4xx = 'http4xx',
  /** Ошибка на стороне бэкенда */
  Http5xx = 'http5xx',
  /** Не смогли классифицировать. Если доля велика — разбираем по логам и уточняем классификацию */
  Unknown = 'unknown',
}

/**
 * Что вызвало обновление пинов. Нужно, чтобы отличать перемещение карты от смены зума
 * и от применения фильтров: у них разные объёмы догружаемых данных и разные ожидания по скорости.
 */
export enum EPinsUpdateTrigger {
  /** Пользователь подвинул карту */
  Pan = 'pan',
  /** Пользователь изменил масштаб */
  Zoom = 'zoom',
  /** Пользователь применил фильтры */
  Filters = 'filters',
}
